import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { getAdminTickets, updateAdminTicket, replyAdminTicket, markAdminSeen, apiError } from "@/lib/api";
import { TicketThread } from "@/components/TicketThread";
import { Loader2, CheckCircle2, RotateCcw } from "lucide-react";
import { logWarn } from "@/lib/log";

const TicketStatusBadge = ({ t }) => (
    <span
        className={`font-mono text-[9px] uppercase tracking-[0.2em] border px-2 py-1 ${
            t.status === "open" ? "text-blaze border-blaze/40" : "text-green-400 border-green-400/40"
        }`}
        data-testid={`ticket-status-${t.ticket_id}`}
    >
        {t.status}
    </span>
);

const AdminTicketItem = ({ t, onToggle, onReply }) => (
    <div key={t.ticket_id} className="px-5 py-4 border-b border-white/5" data-testid={`admin-ticket-${t.ticket_id}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
                <div className="font-mono text-xs text-white/90 font-bold">{t.subject}</div>
                <div className="font-mono text-[10px] text-white/40 mt-1">
                    {t.email} · {new Date(t.created_at).toLocaleString()}
                </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
                <TicketStatusBadge t={t} />
                <button
                    onClick={() => onToggle(t)}
                    className="p-2 border border-white/15 text-white/50 hover:text-white hover:border-white/50 transition-colors"
                    title={t.status === "open" ? "Mark closed" : "Reopen"}
                    data-testid={`ticket-toggle-${t.ticket_id}`}
                >
                    {t.status === "open" ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                        <RotateCcw className="w-3.5 h-3.5" />
                    )}
                </button>
            </div>
        </div>
        <p className="font-mono text-xs text-white/60 mt-3 leading-relaxed whitespace-pre-wrap">
            {t.message}
        </p>
        <TicketThread ticket={t} viewerRole="admin" onReply={(message) => onReply(t, message)} />
    </div>
);

const AdminTicketsList = ({ tickets, onToggle, onReply }) => {
    if (tickets === null) {
        return (
            <div className="p-10 flex justify-center">
                <Loader2 className="w-5 h-5 text-blaze animate-spin" />
            </div>
        );
    }
    return (
        <div className="border border-white/10 bg-surface" data-testid="admin-tickets-panel">
            {tickets.length === 0 ? (
                <div className="p-10 font-mono text-xs text-white/30 text-center" data-testid="admin-tickets-empty">
                    No support tickets yet.
                </div>
            ) : (
                tickets.map((t) => (
                    <AdminTicketItem key={t.ticket_id} t={t} onToggle={onToggle} onReply={onReply} />
                ))
            )}
        </div>
    );
};

export const AdminTickets = () => {
    const [tickets, setTickets] = useState(null);

    const load = useCallback(() => {
        getAdminTickets()
            .then(setTickets)
            .catch((e) => logWarn("admin tickets load failed", e));
    }, []);

    useEffect(() => {
        load();
        markAdminSeen()
            .then(() => window.dispatchEvent(new Event("admin-tickets-seen")))
            .catch(() => {});
    }, [load]);

    const toggle = async (t) => {
        const next = t.status === "open" ? "closed" : "open";
        try {
            await updateAdminTicket(t.ticket_id, next);
            toast.success(`Ticket ${next === "closed" ? "closed" : "reopened"}`);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    const reply = async (t, message) => {
        await replyAdminTicket(t.ticket_id, message);
        toast.success("Reply sent");
        load();
    };

    return <AdminTicketsList tickets={tickets} onToggle={toggle} onReply={reply} />;
};
